import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Switch,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { CalendarEventDto } from '../../../types/calendar';
import { VoiceRecorder } from '../../common/VoiceRecorder';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';

interface AddFeelingEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; scheduledLdt: string; isCompleted: boolean; calendarId?: number }) => void;
  eventToEdit?: CalendarEventDto | null;
  loading: boolean;
  error: string | null;
  calendarId?: number;
  initialScheduledAt?: Date;
}

export const AddFeelingEventModal: React.FC<AddFeelingEventModalProps> = ({
  open,
  onClose,
  onSave,
  eventToEdit,
  loading,
  error,
  calendarId,
  initialScheduledAt,
}) => {
  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(new Date());
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const isEditMode = !!eventToEdit;

  useEffect(() => {
    if (open) {
      if (eventToEdit) {
        setTitle(eventToEdit?.title || '');
        const eventDate = eventToEdit.scheduledLdt ? new Date(eventToEdit.scheduledLdt) : new Date();
        setScheduledAt(eventDate);
        setIsCompleted(eventToEdit.isCompleted ?? false);
        setShowDateTimePicker(true);
      } else {
        setTitle('');
        setScheduledAt(initialScheduledAt ?? new Date());
        setIsCompleted(false);
        setShowDateTimePicker(!!initialScheduledAt);
      }
    }
  }, [open, eventToEdit, initialScheduledAt]);

  const handleSave = () => {
    if (title.trim()) {
      const finalScheduledAt = showDateTimePicker && scheduledAt ? scheduledAt : new Date();
      const finalIsCompleted = showDateTimePicker ? isCompleted : true;
      onSave({
        title,
        scheduledLdt: toLocalIso(finalScheduledAt),
        isCompleted: finalIsCompleted,
        calendarId,
      });
    }
  };

  const handleClose = useCallback(() => {
    setTitle('');
    onClose();
  }, [onClose]);

  const canSave = !loading && title.trim().length > 0 && (!showDateTimePicker || scheduledAt);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-i-doc={ModalIndices.ADD_FEELING_EVENT}>
      <DialogTitle>{isEditMode ? 'Редагувати відчуття' : 'Додати відчуття'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          margin="dense"
          label="Заголовок відчуття"
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={loading}
          sx={{ mt: 2 }}
        />
        <VoiceRecorder
          onRecordingComplete={() => {}}
          onRecordingDelete={() => {}}
          disabled={loading}
          initialAudioBlob={null}
          onError={() => {}}
        />

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showDateTimePicker}
                onChange={(e) => {
                  setShowDateTimePicker(e.target.checked);
                  if (e.target.checked && !scheduledAt) {
                    setScheduledAt(new Date());
                  }
                }}
                disabled={loading}
                size="small"
              />
            }
            label={<Typography variant="body2">Встановити час</Typography>}
          />
          {showDateTimePicker && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
              <DateTimePicker
                label="Запланований час"
                value={scheduledAt}
                onChange={(newValue) => setScheduledAt(newValue)}
                disabled={loading}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          )}
          {showDateTimePicker && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompleted}
                  onChange={(e) => setIsCompleted(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Позначити як виконане"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Скасувати</Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Оновити' : 'Зберегти')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
